import { motion } from "framer-motion";
import { useState } from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowRight,
  Globe,
  ShoppingCart,
  Layout,
  Code,
  Zap,
  Shield,
  Eye,
  Check,
  Mail,
  Rocket,
} from "lucide-react";

const integrationCards = [
  {
    icon: Globe,
    title: "WordPress / WooCommerce",
    features: ["Plugin dedicat", "Activare simplă", "Optimizări automate SEO & GEO"],
  },
  {
    icon: ShoppingCart,
    title: "Shopify",
    features: ["Integrare rapidă", "Optimizare produse & colecții", "Ajustare conținut AI-ready"],
  },
  {
    icon: Layout,
    title: "Wix",
    features: ["Script de conectare", "Optimizare structură pagini", "Monitorizare AI visibility"],
  },
  {
    icon: Code,
    title: "Site custom",
    features: ["Script universal", "Integrare API", "Optimizări controlate"],
  },
];

const steps = [
  { number: "1", title: "Conectezi site-ul", description: "Instalezi pluginul sau script-ul în câteva minute" },
  { number: "2", title: "Primești analiza SEO + GEO", description: "Raport complet cu scor și recomandări" },
  { number: "3", title: "Activezi optimizările recomandate", description: "Implementare controlată cu aprobarea ta" },
];

const Integration = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
      setEmail("");
    }
  };

  return (
    <PageLayout>
      {/* HERO */}
      <section className="py-20 md:py-28 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-6" variant="secondary">
              Coming 2026
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground leading-tight mb-6">
              Integrare automată <span className="text-gradient">în 2026</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-4 max-w-2xl mx-auto">
              OptiRO.ai va permite conectarea rapidă la site-ul tău și aplicarea optimizărilor SEO + GEO automat, fără intervenții tehnice complexe.
            </p>
            <p className="text-muted-foreground mb-8">
              Integrarea completă va fi disponibilă odată cu lansarea oficială din 2026. Înscrie-te pentru acces anticipat.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="gap-2" onClick={() => document.getElementById("early-access")?.scrollIntoView({ behavior: "smooth" })}>
                Înscrie-te pentru Early Access
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="gap-2" onClick={() => document.getElementById("how-it-works-int")?.scrollIntoView({ behavior: "smooth" })}>
                <Eye className="w-4 h-4" />
                Vezi cum funcționează
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ce va fi posibil */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            Ce vei putea face după lansare
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrationCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full relative overflow-hidden">
                  <Badge className="absolute top-3 right-3 text-xs" variant="outline">
                    După lansare
                  </Badge>
                  <CardHeader>
                    <card.icon className="w-8 h-8 text-primary mb-2" />
                    <CardTitle className="text-lg">{card.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {card.features.map((f, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cum va funcționa */}
      <section id="how-it-works-int" className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            Integrare în 3 pași (după lansare)
          </h2>
          <div className="space-y-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-xl bg-background border border-border"
              >
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-8">
            Fără modificări riscante. Fără intervenții manuale complexe. Totul controlat, transparent și monitorizat.
          </p>
        </div>
      </section>

      {/* De ce contează */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            Integrarea nu este doar tehnică. Este strategică.
          </h2>
          <p className="text-muted-foreground mb-6">
            Motoarele AI (ChatGPT, Gemini, AI Overview) prioritizează site-urile clare, structurate și validate extern.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Zap, text: "Analiză structura informațională" },
              { icon: Shield, text: "Identificare lipsuri de autoritate" },
              { icon: Rocket, text: "Aplicare optimizări AI-first" },
              { icon: Eye, text: "Urmărire evoluție scor GEO în timp" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                <item.icon className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="early-access" className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4 max-w-md text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Vrei acces înainte de lansare?
          </h2>
          <p className="text-muted-foreground mb-8">
            Primele conturi vor beneficia de ofertă exclusivă la lansare.
          </p>

          {submitted ? (
            <div className="flex items-center justify-center gap-2 text-primary py-4">
              <Check className="w-5 h-5" />
              <span className="font-medium">Te-ai înscris cu succes!</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                placeholder="email@exemplu.ro"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                required
              />
              <Button type="submit" size="lg" className="whitespace-nowrap">
                <Mail className="w-4 h-4 mr-2" />
                Înscrie-te
              </Button>
            </form>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export default Integration;
